import React, { useEffect, useCallback } from 'react';
import {
  XMarkIcon,
  InformationCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  CheckCircleIcon
} from '@heroicons/react/20/solid';

const Dialog = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  title, 
  children, 
  confirmText = '확인',
  cancelText = '취소',
  type = 'info', // 'info', 'warning', 'error', 'success'
  showConfirm = true,
  showCancel = true 
}) => {
  // ESC 키와 Enter 키 핸들링
  const handleKeyDown = useCallback((event) => {
    if (!isOpen) return;
    
    if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
    } else if (event.key === 'Enter' && showConfirm && onConfirm) {
      event.preventDefault();
      onConfirm();
    }
  }, [isOpen, onClose, onConfirm, showConfirm]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      // body 스크롤 방지
      document.body.style.overflow = 'hidden';
    } else {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleKeyDown]);

  // Dim 영역 클릭시 닫기
  const handleBackdropClick = (event) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  const getDialogTypeClass = () => {
    switch (type) {
      case 'warning':
        return 'border-warning';
      case 'error':
        return 'border-error';
      case 'success':
        return 'border-success';
      default:
        return 'border-primary';
    }
  };

  const getTypeIcon = () => {
    const iconClass = "h-6 w-6 mr-3";
    switch (type) {
      case 'warning':
        return <ExclamationTriangleIcon className={`${iconClass} text-warning`} />;
      case 'error':
        return <XCircleIcon className={`${iconClass} text-error`} />;
      case 'success':
        return <CheckCircleIcon className={`${iconClass} text-success`} />;
      default:
        return <InformationCircleIcon className={`${iconClass} text-primary`} />;
    }
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 transition-opacity duration-200 ease-out"
      onClick={handleBackdropClick}
      style={{ animation: 'fadeIn 0.2s ease-out' }}
    >
      <div 
        className={`bg-base-100 rounded-lg shadow-xl border-t-4 ${getDialogTypeClass()} min-w-80 max-w-md mx-4 relative transform transition-all duration-200 ease-out`}
        onClick={(e) => e.stopPropagation()}
        style={{ animation: 'slideInUp 0.2s ease-out' }}
      >
        {/* 헤더 */}
        <div className="flex items-center justify-between p-6 pb-4">
          <div className="flex items-center flex-1">
            {getTypeIcon()}
            {title && (
              <h3 className="text-lg font-semibold text-base-content">{title}</h3>
            )}
          </div>
          <button
            className="btn btn-ghost btn-sm btn-square ml-2 hover:bg-base-200"
            onClick={onClose}
            title="닫기 (ESC)"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* 본문 */}
        <div className="px-6 py-2">
          {children}
        </div>

        {/* 푸터 버튼들 */}
        {(showConfirm || showCancel) && (
          <div className="flex justify-end gap-3 p-6 pt-4 bg-base-100 rounded-b-lg">
            {showCancel && (
              <button
                className="btn btn-ghost hover:bg-base-200"
                onClick={onClose}
                title="취소 (ESC)"
              >
                {cancelText}
              </button>
            )}
            {showConfirm && onConfirm && (
              <button
                className="btn btn-primary hover:btn-primary-focus"
                onClick={onConfirm}
                title="확인 (Enter)"
              >
                {confirmText}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Dialog;
